import React from 'react';
import { Box, Flex, HStack, Text, Tooltip } from '@chakra-ui/react';
import { Handle, Position } from 'reactflow';
import { FiZap, FiEdit, FiCopy, FiTrash2 } from 'react-icons/fi';
import { IconButton } from '@chakra-ui/react';

const ConditionNode = ({ data }) => {
  const getConditionSummary = () => {
    if (!data.eventSource) return 'Not configured';
    
    let summary = data.eventSource;
    const conditionType = data.conditionLogic || 'occurred';
    
    // Build summary based on condition type
    if (conditionType === 'occurred') {
      summary += ' occurred';
    } else if (conditionType === 'not_occurred') {
      summary += ' not occurred';
    } else if (conditionType === 'within_time') {
      summary += ` within ${data.timeWindowValue || '1'} ${data.timeWindowUnit || 'days'}`;
    } else if (conditionType === 'attribute_check') {
      const operator = data.operator === 'equals' ? '=' : 
                      data.operator === 'not_equals' ? '≠' :
                      data.operator === 'greater_than' ? '>' :
                      data.operator === 'less_than' ? '<' :
                      data.operator === 'contains' ? '⊃' :
                      data.operator;
      summary += ` (${data.attributeName || 'attribute'} ${operator} "${data.attributeValue || 'value'}")`;
    } else if (conditionType === 'count_of_events') {
      const countOp = data.countOperator === 'equals' ? '=' :
                      data.countOperator === 'not_equals' ? '≠' :
                      data.countOperator === 'greater_than' ? '>' :
                      data.countOperator === 'less_than' ? '<' :
                      data.countOperator === 'greater_than_or_equal' ? '≥' :
                      data.countOperator === 'less_than_or_equal' ? '≤' :
                      data.countOperator;
      summary += ` count ${countOp} ${data.countValue || '0'}`;
    }
    
    // Add attribute matching info if enabled (for any condition type)
    if (data.enableAttributeMatching && data.matchAttribute) {
      const refType = data.referenceType || 'static';
      let refValue = '';
      
      if (refType === 'previous_event') {
        refValue = data.previousEventRef || 'previous.value';
      } else if (refType === 'message_variable') {
        refValue = `{{${data.messageVariable || 'variable'}}}`;
      } else {
        refValue = `"${data.staticValue || 'value'}"`;
      }
      
      const comparison = data.comparisonType === 'equals' ? '=' : 
                         data.comparisonType === 'greater_than' ? '>' :
                         data.comparisonType === 'less_than' ? '<' :
                         data.comparisonType === 'contains' ? '⊃' :
                         data.comparisonType === 'starts_with' ? '⊃^' :
                         data.comparisonType;
      
      summary += ` & ${data.matchAttribute} ${comparison} ${refValue}`;
    }
    
    return summary;
  };

  return (
    <Tooltip label="Condition: Check if event occurred" placement="right">
      <Box
        bg="white"
        borderWidth="2px"
        borderColor={data.selected ? 'orange.500' : '#fbaf3f'}
        borderRadius="xl"
        boxShadow={data.selected ? 'xl' : 'lg'}
        minW="280px"
      >
        <Handle type="target" position={Position.Left} style={{ background: 'black' }} />
        
        <Flex
          bg="#fbaf3f"
          color="white"
          px={4}
          py={2.5}
          borderTopRadius="xl"
          align="center"
          justify="space-between"
        >
          <HStack spacing={2}>
            <FiZap />
            <Text noOfLines={1} fontSize="sm" fontWeight="medium">Condition: Check Event</Text>
          </HStack>
          <HStack spacing={1}>
            <IconButton
              size="xs"
              variant="ghost"
              color="white"
              icon={<FiEdit />}
              onClick={(e) => { e.stopPropagation(); data.onEdit?.(data.id); }}
              _hover={{ bg: 'whiteAlpha.200' }}
            />
            <IconButton
              size="xs"
              variant="ghost"
              color="white"
              icon={<FiCopy />}
              onClick={(e) => { e.stopPropagation(); data.onDuplicate?.(data.id); }}
              _hover={{ bg: 'whiteAlpha.200' }}
            />
            <IconButton
              size="xs"
              variant="ghost"
              color="white"
              icon={<FiTrash2 />}
              onClick={(e) => { e.stopPropagation(); data.onDelete?.(data.id); }}
              _hover={{ bg: 'whiteAlpha.200' }}
            />
          </HStack>
        </Flex>

        <Box p={3.5}>
          <Text fontSize="sm" fontWeight="medium" mb={2}>
            {data.label || 'Check Event'}
          </Text>
          
          {/* Condition Summary */}
          <Box
            fontSize="xs"
            bg="orange.50"
            border="1px"
            borderColor="orange.200"
            rounded="md"
            px={2}
            py={1.5}
            mb={3}
            wordBreak="break-words"
          >
            {getConditionSummary()}
          </Box>

          {/* Fallback Indicator */}
          {data.fallbackEnabled && (
            <Box
              fontSize="xs"
              bg="yellow.50"
              border="1px"
              borderColor="yellow.300"
              rounded="md"
              px={2}
              py={1}
              mb={2}
            >
              ⚠️ Fallback enabled: No path if data unavailable
            </Box>
          )}

          {/* Branch Paths */}
          <Flex gap={2} mt={3}>
            <Box flex={1} textAlign="center" borderRight="1px" borderColor="gray.200">
              <Text fontSize="xs" fontWeight="medium" color="green.700" mb={1}>
                ✓ {data.yesLabel || 'Yes'}
              </Text>
              <Handle
                type="source"
                position={Position.Right}
                id="yes"
                style={{ background: '#48BB78', top: '40%' }}
              />
            </Box>
            <Box flex={1} textAlign="center">
              <Text fontSize="xs" fontWeight="medium" color="red.700" mb={1}>
                ✗ {data.noLabel || 'No'}
              </Text>
              <Handle
                type="source"
                position={Position.Right}
                id="no"
                style={{ background: '#EF4444', top: '60%' }}
              />
            </Box>
          </Flex>
        </Box>
      </Box>
    </Tooltip>
  );
};

export default ConditionNode;